// components/Tour/TourStep.js
import React, { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styles from './TourStep.module.css';

const TourStep = ({ step, currentStep, totalSteps, onNext, onPrev, onSkip, onFinish }) => {
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [arrowPosition, setArrowPosition] = useState({});
  const [placement, setPlacement] = useState('bottom');
  const tooltipRef = useRef(null);
  const targetRef = useRef(null);

  const calculatePosition = useCallback(() => {
    const targetElement = document.querySelector(step.targetSelector);
    if (!targetElement) {
      console.warn(`Tour target "${step.targetSelector}" not found`);
      return;
    }

    targetRef.current = targetElement;
    const targetRect = targetElement.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect() || { width: 320, height: 200 };
    
    const spacing = 16;
    const arrowSize = 12;
    
    // Calculate best placement
    const placements = {
      bottom: {
        top: targetRect.bottom + spacing,
        left: targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2),
        arrow: { top: -arrowSize, left: '50%', transform: 'translateX(-50%)' },
        arrowDirection: 'up',
      },
      top: {
        top: targetRect.top - tooltipRect.height - spacing,
        left: targetRect.left + (targetRect.width / 2) - (tooltipRect.width / 2),
        arrow: { bottom: -arrowSize, left: '50%', transform: 'translateX(-50%)' },
        arrowDirection: 'down',
      },
      right: {
        top: targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2),
        left: targetRect.right + spacing,
        arrow: { left: -arrowSize, top: '50%', transform: 'translateY(-50%)' },
        arrowDirection: 'left',
      },
      left: {
        top: targetRect.top + (targetRect.height / 2) - (tooltipRect.height / 2),
        left: targetRect.left - tooltipRect.width - spacing,
        arrow: { right: -arrowSize, top: '50%', transform: 'translateY(-50%)' },
        arrowDirection: 'right',
      },
    };

    // Choose best placement based on available space
    let bestPlacement = step.placement || 'bottom';
    const viewport = { width: window.innerWidth, height: window.innerHeight };
    
    // Check if preferred placement fits
    if (step.placement && placements[step.placement]) {
      const pos = placements[step.placement];
      if (
        pos.top >= 10 &&
        pos.left >= 10 &&
        pos.left + tooltipRect.width <= viewport.width - 10 &&
        pos.top + tooltipRect.height <= viewport.height - 10
      ) {
        bestPlacement = step.placement;
      } else {
        // Try other placements
        const order = ['bottom', 'top', 'right', 'left'];
        for (const place of order) {
          const p = placements[place];
          if (
            p.top >= 10 &&
            p.left >= 10 &&
            p.left + tooltipRect.width <= viewport.width - 10 &&
            p.top + tooltipRect.height <= viewport.height - 10
          ) {
            bestPlacement = place;
            break;
          }
        }
      }
    }

    const selectedPlacement = placements[bestPlacement];
    
    // Clamp to viewport
    const clampedLeft = Math.max(10, Math.min(
      selectedPlacement.left,
      viewport.width - tooltipRect.width - 10
    ));
    const clampedTop = Math.max(10, Math.min(
      selectedPlacement.top,
      viewport.height - tooltipRect.height - 10
    ));

    setPosition({
      top: clampedTop + window.scrollY,
      left: clampedLeft + window.scrollX,
    });
    setArrowPosition(selectedPlacement.arrow);
    setPlacement(bestPlacement);

    // Highlight target element
    targetElement.style.position = 'relative';
    targetElement.style.zIndex = '10001';
    targetElement.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.5)';
    targetElement.style.borderRadius = '4px';
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [step]);

  useEffect(() => {
    calculatePosition();
    
    const handleResize = () => calculatePosition();
    const handleScroll = () => calculatePosition();
    
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll);
      
      // Clean up target element styling
      if (targetRef.current) {
        targetRef.current.style.position = '';
        targetRef.current.style.zIndex = '';
        targetRef.current.style.boxShadow = '';
        targetRef.current.style.borderRadius = '';
      }
    };
  }, [calculatePosition]);

  const isLast = currentStep === totalSteps - 1;

  return createPortal(
    <>
      {/* Overlay */}
      <div className={styles.overlay} onClick={(e) => e.stopPropagation()} />
      
      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className={styles.tooltip}
        style={{
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
      >
        {/* Arrow */}
        <div
          className={`${styles.arrow} ${styles[`arrow${placement}`]}`}
          style={arrowPosition}
        />
        
        {/* Content */}
        <div className={styles.content}>
          {step.icon && (
            <div className={styles.iconWrapper}>
              {step.icon}
            </div>
          )}
          
          <h3 className={styles.title}>{step.title}</h3>
          <p className={styles.description}>{step.description}</p>
          
          {step.image && (
            <img src={step.image} alt="" className={styles.image} />
          )}
        </div>
        
        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.progress}>
            {Array.from({ length: totalSteps }, (_, i) => (
              <div
                key={i}
                className={`${styles.dot} ${i === currentStep ? styles.activeDot : ''} ${
                  i < currentStep ? styles.completedDot : ''
                }`}
              />
            ))}
          </div>
          
          <div className={styles.actions}>
            <button className={styles.skipBtn} onClick={onSkip}>
              Skip
            </button>
            
            {currentStep > 0 && (
              <button className={styles.prevBtn} onClick={onPrev}>
                Back
              </button>
            )}
            
            {isLast ? (
              <button className={styles.finishBtn} onClick={onFinish}>
                Got it!
              </button>
            ) : (
              <button className={styles.nextBtn} onClick={onNext}>
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default TourStep;